import React, { useCallback } from "react";
import { ProductCatalog } from "@/components/products/ProductCatalog";
import { type SelectedProduct } from "@/components/products/ProductGrid";
import { Product, ProductSelection } from "@/types/product";
import type { QuoteProduct } from "@onsemble/shared";
import { cn } from "@/lib/utils";



interface ProductSelectionSectionProps {
  selectedProducts: ProductSelection[];
  onProductsChange: (products: ProductSelection[]) => void;
  showPricing?: boolean; // Show pricing and availability (orders vs quotes)
  disabled?: boolean;
  className?: string;
}

export const ProductSelectionSection: React.FC<
  ProductSelectionSectionProps
> = ({
  selectedProducts,
  onProductsChange,
  showPricing = false,
  disabled = false,
  className,
}) => {
  // Convert ProductSelection to SelectedProduct for catalog
  const catalogSelectedProducts: SelectedProduct[] = selectedProducts.map(
    (p) => ({
      productId: p.productId,
      quantity: p.quantity,
      unitPrice: "unitPrice" in p ? p.unitPrice : undefined,
    }),
  );

  // Handle product selection from the catalog
  const handleProductSelect = useCallback(
    (product: Product, quantity: number) => {
      if (quantity === 0) {
        // Remove product
        onProductsChange(
          selectedProducts.filter((p) => p.productId !== product.uid),
        );
      } else {
        // Add or update product
        const existingIndex = selectedProducts.findIndex(
          (p) => p.productId === product.uid,
        );

        let newProduct: ProductSelection;

        if (showPricing) {
          // For orders: include complete product data for OrderForm
          const unitPrice = product.currentPrice;

          newProduct = {
            productId: product.uid,
            quantity: quantity,
            notes: "",
            unitPrice: unitPrice,
            // Include full product data to avoid lookup issues in OrderForm
            __fullProduct: product,
          } as ProductSelection & {
            unitPrice?: number;
            __fullProduct?: Product;
          };
        } else {
          // For quotes: just basic product info
          newProduct = {
            productId: product.uid,
            quantity: quantity,
            notes: "",
          } as QuoteProduct;
        }

        if (existingIndex >= 0) {
          const updated = [...selectedProducts];
          updated[existingIndex] = newProduct;
          onProductsChange(updated);
        } else {
          onProductsChange([...selectedProducts, newProduct]);
        }
      }
    },
    [selectedProducts, onProductsChange, showPricing],
  );

  return (
    <div className={cn("space-y-6", className)}>
      {/* Product Catalog */}
      <ProductCatalog
        selectedProducts={catalogSelectedProducts}
        onProductSelect={handleProductSelect}
        showCachedPricing={showPricing}
        disabled={disabled}
      />
    </div>
  );
};
